/* eslint-disable */
import React from 'react';

import ManagerRepository from 'coreClasses/ManagerRepository';
import type {ExpressButtonStyleConfig as IGooglePayButtonStyleConfig} from 'coreStore/data/basket/types';
import {promiseWithRetry} from 'coreUtils/utils';

import {IGooglePayButton} from './types/IGooglePayButton';
import {GooglePaymentsAPI} from './types/TGooglePayAPI';

type TExtendedPaymentDataRequest = GooglePaymentsAPI.PaymentDataRequest & {
	success?: boolean;
};

type TExtendedIsReadyToPayRequest = GooglePaymentsAPI.IsReadyToPayRequest & {
	emailRequired?: boolean;
	shippingAddressRequired?: boolean;
	shippingAddressParameters?: GooglePaymentsAPI.ShippingAddressParameters;
};

type TIframeConfig = {
	width?: string | number;
	height?: string | number;
};

/**
 * Represents GooglePayWorldpayHandler
 */
class GooglePayWorldpayHandler {
	private FALLBACK_DDC_TIME: number;
	private DDC_RETRIES_COUNT: number;
	private config: IGooglePayButton = {
		googlePayGateway: '',
		googlePayMerchantId: '',
		googlePayGatewayMerchantId: '',
		googlePayMerchantName: '',
		worldpayCardinalCommerceURL: '',
		allowedCardNetworks: [],
		allowedCardAuthMethods: [],
		countryCode: '',
		forceProdEnvironment: false
	};
	private isDebug: boolean;
	private baseRequest: TExtendedIsReadyToPayRequest;
	private allowedCardNetworks: GooglePaymentsAPI.CardNetwork[] = [];
	private allowedCardAuthMethods: GooglePaymentsAPI.CardAuthMethod[] = [];
	private tokenizationSpecification: GooglePaymentsAPI.PaymentGatewayTokenizationSpecification = {} as GooglePaymentsAPI.PaymentGatewayTokenizationSpecification;
	private baseCardPaymentMethod: GooglePaymentsAPI.IsReadyToPayPaymentMethodSpecification = {} as GooglePaymentsAPI.IsReadyToPayPaymentMethodSpecification;
	private cardPaymentMethod: GooglePaymentsAPI.PaymentMethodSpecification = {} as GooglePaymentsAPI.PaymentMethodSpecification;
	private paymentsClient: GooglePaymentsAPI.PaymentsClient = null;
	private paymentDataCallbacks: GooglePaymentsAPI.PaymentDataCallbacks = {};
	private iframeLayout: string = '';
	private basketId: string = '';
	private managerRepository: ManagerRepository;

	/**
	 * @constructor
	 * @param {ManagerRepository} managerRepository - manager repository
	 * @param {IGooglePayButton} config - GooglePay configuration
	 * @param {boolean} isDebug - is debugging mode enabled
	 */
	constructor(managerRepository: ManagerRepository, config: IGooglePayButton, isDebug: boolean = false) {
		this.FALLBACK_DDC_TIME = 12000; // 12 sec
		this.DDC_RETRIES_COUNT = 3;
		this.config = (config || {}) as IGooglePayButton;
		this.isDebug = isDebug && !this.config.forceProdEnvironment;
		this.managerRepository = managerRepository;
		this.baseRequest = {
			apiVersion: 2,
			apiVersionMinor: 0,
			emailRequired: true
		} as TExtendedIsReadyToPayRequest;
		this.allowedCardNetworks = this.config.allowedCardNetworks as GooglePaymentsAPI.CardNetwork[];
		this.allowedCardAuthMethods = this.config.allowedCardAuthMethods as GooglePaymentsAPI.CardAuthMethod[];
		this.baseCardPaymentMethod = {
			type: 'CARD',
			parameters: {
				allowedAuthMethods: this.allowedCardAuthMethods,
				allowedCardNetworks: this.allowedCardNetworks,
				billingAddressRequired: true,
				billingAddressParameters: {
					format: 'FULL',
					phoneNumberRequired: true
				}
			}
		};
		this.tokenizationSpecification = {
			type: 'PAYMENT_GATEWAY',
			parameters: {
				gateway: this.config.googlePayGateway,
				gatewayMerchantId: this.config.googlePayGatewayMerchantId
			}
		};
		this.cardPaymentMethod = Object.assign({},
			this.baseCardPaymentMethod, {
				tokenizationSpecification: this.tokenizationSpecification
			}
		) as GooglePaymentsAPI.PaymentMethodSpecification;
		this.paymentDataCallbacks = {
			onPaymentDataChanged: this.onPaymentDataChanged.bind(this)
		};
		this.paymentsClient = null;
		this.iframeLayout = '';
	}

	/**
	 * Add a Google Pay purchase button alongside an existing checkout button
	 * @param {Function} onClick - handle click on button
	 * @param {Object} googlePayButtonStyleConfig - Google Pay button styles config
	 *
	 * @see {@link https://developers.google.com/pay/api/web/reference/client#createButton|createButton()}
	 * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#ButtonOptions|Button options}
	 * @see {@link https://developers.google.com/pay/api/web/guides/brand-guidelines|Google Pay brand guidelines}
	 * @returns {HTMLDivElement} Google Pay button
	 */
	getButton(onClick, googlePayButtonStyleConfig: IGooglePayButtonStyleConfig = {}) {
		const paymentsClient = this.getGooglePaymentsClient();
		const defaultButtonStyleConfig = {
			buttonColor: 'default',
  			buttonType: 'plain',
  			buttonRadius: 0,
		};
		const overwriteButtonStyleConfig = {
			...defaultButtonStyleConfig,
			...googlePayButtonStyleConfig
		};
		const button = paymentsClient.createButton({
			onClick: onClick,
			allowedPaymentMethods: [this.baseCardPaymentMethod],
			buttonSizeMode: 'fill',
			...overwriteButtonStyleConfig,
		} as GooglePaymentsAPI.ButtonOptions);

		return button;
	}

	/**
	 * Initialize Google PaymentsClient after Google-hosted JavaScript has loaded
	 *
 	 * @see {@link https://developers.google.com/pay/api/web/reference/client#isReadyToPay|isReadyToPay()}
	 * @param {Basket} basket - current basket
	 * @returns {Promise} result of isReadyToPay
	 */
	async onGooglePayLoaded() {
		const paymentsClient = this.getGooglePaymentsClient();
		const request = await this.getGoogleIsReadyToPayRequest();

		return paymentsClient.isReadyToPay(request)
			.then((response) => {
				return Promise.resolve(response.result);
			})
			.catch(function(err) {
				// show error in developer console for debugging
				console.error(err);

				return Promise.reject(err);
			});
	}

	/**
	 * Show Google Pay payment sheet when Google Pay payment button is clicked
	 * @param {String} productId - product ID
	 * @param {Function} handleClickCallback - callback function to handle click event
	 * @param {String} basketId - basket ID
	 */
	async handlePayment(productId, handleClickCallback, basketId) {
		const paymentInstrumentPayload = {
			paymentMethodId: 'apmgw_GooglePay',
		};
		let basketResponse = null;

		if (productId) {
			basketResponse = await this.managerRepository.getCommerceApi().shopperBaskets.createBasket({
				body: {
					temporaryBasket: true,
					paymentInstruments: [paymentInstrumentPayload],
					productItems: [{
						productId: productId,
						quantity: 1
					}]
				},
				parameters: {
					temporary: true
				}
			});

			this.basketId = basketResponse.basketId;
		} else if (basketId) {
			this.basketId = basketId;

			basketResponse = await this.managerRepository.getCommerceApi().shopperBaskets.addPaymentInstrumentToBasket({
				parameters: {
					basketId: this.basketId
				},
				body: paymentInstrumentPayload
			});
		}

		const paymentDataRequest = await this.getGooglePaymentDataRequest(basketResponse);

		if (!paymentDataRequest.success) {
			window.location.href =  this.managerRepository.getSfccAPI().generateEndpointUrl('COCart-Show', true);
		} else {
			const paymentsClient = this.getGooglePaymentsClient();

			delete paymentDataRequest.success;

			paymentsClient.loadPaymentData(paymentDataRequest)
				.then((paymentData) => {
					this.processPayment(paymentData, handleClickCallback);
				})
				.catch((err) => {});
			}
	}

	/**
	 * Process payment data returned by the Google Pay API
	 * @see {@link https://developers.google.com/pay/api/web/reference/response-objects#PaymentMethodData|PaymentMethodData}
	 * @param {GooglePaymentsAPI.PaymentMethodData} paymentData - This is a response object that's returned by Google after a payer approves payment.
	 */
	private async processPayment(paymentData, handleClickCallback) {
		let tokenizationData = null;
		let basketId = this.basketId;

		try {
			tokenizationData = JSON.parse(paymentData.paymentMethodData.tokenizationData.token);
		} catch (e) {}

		const parsedShippingName = this.parseGoogleName(paymentData.shippingAddress.name);
		const parsedBillingName = this.parseGoogleName(paymentData.paymentMethodData.info.billingAddress.name);
		const dfReferenceId = await this.getThreeDSDDC();

		const paymentAuthorize = await this.managerRepository.getSfccAPI().googlePayPaymentAuthorized({
			email: paymentData.email,
			shippingAddress: {
				firstName: parsedShippingName.firstName,
				lastName: parsedShippingName.lastName,
				address1: paymentData.shippingAddress.address1,
				address2: paymentData.shippingAddress.address2,
				city: paymentData.shippingAddress.locality,
				postalCode: paymentData.shippingAddress.postalCode,
				stateCode: paymentData.shippingAddress.administrativeArea,
				countryCode: paymentData.shippingAddress.countryCode,
				phoneNumber: paymentData.shippingAddress.phoneNumber
			},
			billingAddress: {
				firstName: parsedBillingName.firstName,
				lastName: parsedBillingName.lastName,
				address1: paymentData.paymentMethodData.info.billingAddress.address1,
				address2: paymentData.paymentMethodData.info.billingAddress.address2,
				city: paymentData.paymentMethodData.info.billingAddress.locality,
				postalCode: paymentData.paymentMethodData.info.billingAddress.postalCode,
				stateCode: paymentData.paymentMethodData.info.billingAddress.administrativeArea,
				countryCode: paymentData.paymentMethodData.info.billingAddress.countryCode,
				phoneNumber: paymentData.paymentMethodData.info.billingAddress.phoneNumber
			},
			shippingOptionData: paymentData.shippingOptionData,
			tokenizationData: tokenizationData,
			dfReferenceId: dfReferenceId,
			basketId: basketId
		});

		if (paymentAuthorize.success && paymentAuthorize.challengeDetails?.threeDSJWT) {
			/* We need to redirect user with POST method using form and fields. As using form is the only options
				* to do make POST redirect we are forced to emulate redirect in this. Link to documentation
				* https://developerengine.fisglobal.com/apis/wpg/directintegration/cardinalsecuretest#first-xml-payment-response-3ds-challenge
				*/

			if (typeof handleClickCallback === 'function') {
				handleClickCallback(paymentAuthorize.challengeDetails.threeDSJWT, this.config.worldpayCardinalCommerceURL, dfReferenceId);
			}
		} else if (paymentAuthorize.success && paymentAuthorize.continueURL) {
			window.location.href = paymentAuthorize.continueURL;
		} else {
			this.managerRepository.getCommerceApi().shopperBaskets.deleteBasket({
				parameters: {
					basketId: this.basketId
				}
			});
		}
	}

	/**
	 * Configure your site's support for payment methods supported by the Google Pay
	 * API.
	 *
	 * Each member of allowedPaymentMethods should contain only the required fields,
	 * allowing reuse of this base request when determining a viewer's ability
	 * to pay and later requesting a supported payment method
	 *
	 * @param {Basket} basket - current basket
	 * @returns {object} Google Pay API version, payment methods supported by the site
	 */
	private getGoogleIsReadyToPayRequest(): TExtendedIsReadyToPayRequest {
		const paymentDataRequest = Object.assign({}, this.baseRequest) as TExtendedIsReadyToPayRequest;

		paymentDataRequest.emailRequired = true;
		paymentDataRequest.shippingAddressRequired = true;
		paymentDataRequest.shippingAddressParameters = this.getShippingAddressParameters();
		paymentDataRequest.allowedPaymentMethods = [this.baseCardPaymentMethod];

		return paymentDataRequest;
	}

	/**
	 * Configure support for the Google Pay API
	 * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#PaymentDataRequest|PaymentDataRequest}
	 * @returns {GooglePaymentsAPI.PaymentDataRequest} paymentDataRequest fields
	 */
	private async getGooglePaymentDataRequest(basketResponse: Record<string, any>): Promise<TExtendedPaymentDataRequest> {
		const paymentDataRequest = Object.assign({}, this.baseRequest) as TExtendedPaymentDataRequest;
		const shippingOptionParameters = await this.getShippingOptionParameters(basketResponse.c_shippingMethodModels)

		paymentDataRequest.allowedPaymentMethods = [this.cardPaymentMethod];
		paymentDataRequest.transactionInfo = this.getGoogleTransactionInfo(basketResponse);
		paymentDataRequest.merchantInfo = {
			// a merchant ID is available for a production environment after approval by Google
			merchantId: this.config.googlePayMerchantId,
			merchantName: this.config.googlePayMerchantName
		};
		paymentDataRequest.shippingAddressRequired = true;
		paymentDataRequest.shippingAddressParameters = this.getShippingAddressParameters(basketResponse);
		paymentDataRequest.shippingOptionRequired = true;
		paymentDataRequest.shippingOptionParameters = shippingOptionParameters;
		paymentDataRequest.callbackIntents = ['SHIPPING_OPTION', 'SHIPPING_ADDRESS'];
		paymentDataRequest.success = basketResponse?.c_isAllProductLineItemsAvailable;

		return paymentDataRequest;
	}

	/**
	 * Provide Google Pay API with a shipping address options and restrictions
	 *
	 * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#ShippingAddressParameters|ShippingAddressParameters}
	 * @param {Object} [basketResponse] - basket response result
	 * @returns {Object} shipping info, suitable for use as shippingAddressParameters property of PaymentDataRequest
	 */
	private getShippingAddressParameters(basketResponse?: Record<string, any>): GooglePaymentsAPI.ShippingAddressParameters {
		return {
			phoneNumberRequired: true,
			allowedCountryCodes: [basketResponse ? basketResponse.c_countryCode : this.config.countryCode]
		};
	}

	/**
	 * Provide Google Pay API with a shipping options
	 *
	 * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#ShippingOptionParameters|ShippingOptionParameters}
	 * @returns {GooglePaymentsAPI.ShippingOptionParameters} shipping options info, suitable for use as shippingOptionParameters property of PaymentDataRequest
	 */
	private async getShippingOptionParameters(shippingMethods: Array<Record<string, any>> | undefined): Promise<GooglePaymentsAPI.ShippingOptionParameters> {
		const shippingOptionParameters = {} as GooglePaymentsAPI.ShippingOptionParameters;

		if (shippingMethods) {
			shippingOptionParameters.defaultSelectedOptionId = shippingMethods[0].id;
			shippingOptionParameters.shippingOptions = shippingMethods as GooglePaymentsAPI.SelectionOption[];
		}

		return shippingOptionParameters;
	}

	/**
	 * Provide Google Pay API with a payment amount, currency, and amount status
	 *
	 * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#TransactionInfo|TransactionInfo}
	 * @param {object} basket - basket
	 * @returns {GooglePaymentsAPI.TransactionInfo} transaction info, suitable for use as transactionInfo property of PaymentDataRequest
	 */

	private getGoogleTransactionInfo(response: Record<string, any>): GooglePaymentsAPI.TransactionInfo {
		return {
			countryCode: response?.c_countryCode || this.config.countryCode,
			currencyCode: response?.currency,
			totalPrice: String(response?.orderTotal || response?.productTotal),
			totalPriceStatus: 'FINAL',
			totalPriceLabel: 'Total'
		};
	}

	/**
	 * Return an active PaymentsClient or initialize
	 *
	 * @see {@link https://developers.google.com/pay/api/web/reference/client#PaymentsClient|PaymentsClient constructor}
	 * @returns {GooglePaymentsAPI.PaymentsClient} Google Pay API client
	 */
	private getGooglePaymentsClient() {
		if (this.paymentsClient === null) {
			this.paymentsClient = new google.payments.api.PaymentsClient({
				environment: this.isDebug ? 'TEST' : 'PRODUCTION',
				paymentDataCallbacks: this.paymentDataCallbacks
			}) as GooglePaymentsAPI.PaymentsClient;
		}

		return this.paymentsClient;
	}

	/**
	 * Callback function. Triggered when payment data on payment sheet was changed
	 * @see {@link https://developers.google.com/pay/api/web/reference/response-objects#IntermediatePaymentData|IntermediatePaymentData}
	 * @see {@link https://developers.google.com/pay/api/web/reference/response-objects#OfferData|OfferData}
	 * @see {@link https://developers.google.com/pay/api/web/reference/response-objects#IntermediateAddress|IntermediateAddress}
	 * @see {@link https://developers.google.com/pay/api/web/reference/response-objects#SelectionOptionData|SelectionOptionData}
	 * @param {GooglePaymentsAPI.IntermediatePaymentData} intermediatePaymentData - intermediate payment data
	 * @param {string} intermediatePaymentData.callbackTrigger - Describes the reason for which payment data callback was invoked.
		INITIALIZE
		SHIPPING_ADDRESS
		SHIPPING_OPTION
		OFFER
	 * @param {GooglePaymentsAPI.OfferData} intermediatePaymentData.offerData - The promotional code provided by the user.
	 * @param {GooglePaymentsAPI.IntermediateAddress} intermediatePaymentData.shippingAddress - The selected address in the payment sheet.
	 * @param {GooglePaymentsAPI.SelectionOptionData} intermediatePaymentData.shippingOptionData - The selected shipping option in the payment sheet.
	 * @returns {Promise} Promise of resolving new data object
	 */
	private onPaymentDataChanged(intermediatePaymentData: GooglePaymentsAPI.IntermediatePaymentData) {
		return new Promise((resolve) => {
			if (intermediatePaymentData.callbackTrigger === 'SHIPPING_OPTION') {
				const response = this.managerRepository.getCommerceApi().shopperBaskets.updateShippingMethodForShipment({
					body: {
						id: intermediatePaymentData?.shippingOptionData.id
					},
					parameters: {
						basketId: this.basketId,
						shipmentId: 'me'
					},
				}).then((response) => {
					resolve({
						newTransactionInfo: this.getGoogleTransactionInfo(response)
					});
				});
			} else if (intermediatePaymentData.callbackTrigger === 'SHIPPING_ADDRESS' || intermediatePaymentData.callbackTrigger === 'INITIALIZE') {
				const response = this.managerRepository.getCommerceApi().shopperBaskets.updateShippingAddressForShipment({
					body: {
						city: intermediatePaymentData.shippingAddress.locality,
						postalCode: intermediatePaymentData.shippingAddress.postalCode,
						stateCode: intermediatePaymentData.shippingAddress.administrativeArea,
						countryCode: intermediatePaymentData.shippingAddress.countryCode
					},
					parameters: {
						basketId: this.basketId,
						shipmentId: 'me'
					},
				}).then((response) => {
					resolve({
						newTransactionInfo: this.getGoogleTransactionInfo(response)
					});
				});
			} else {
				resolve({});
			}
		});
	}

	/**
	 * Simulate submiting form with provided URL and data
	 * @param {Object} args - aruments for submit form
	 * @param {string} args.url - url to redirect
	 * @param {string} args.method - HTTP method to use
	 * @param {Object} args.fields - object with values that should be populated as form fields
	 */
	private formSubmitter(args) {
		const form = React.createElement('form', {action: args.url, method: args.method}) as any;

		Object.entries(args.fields).forEach(([name, value]) => {
			const input = React.createElement('input', {name, value});
			form.props.children.push(input);
		});

		document.body.appendChild(form);

		form.props.onSubmit = () => {
			if (form && form.parentNode === document.body) {
				document.body.removeChild(form);
			}
		};

		form.props.onSubmit();
	}

	private createIframe({ src = 'about:blank', body, iframeConfig = {} }: { src?: string; body?: string; iframeConfig?: TIframeConfig }) {
		const iframe = document.createElement('iframe');

		iframe.setAttribute('src', src);
		iframe.style.width = (iframeConfig.width || '0') + 'px';
		iframe.style.height = (iframeConfig.height || '0') + 'px';
		iframe.style.display = 'none';

		document.body.appendChild(iframe);

		if (body) {
			iframe.contentWindow.document.body.innerHTML = body;
		}

		return iframe;
	}

	/**
	 * Get DDC iframe form in string format
	 * @param {string} ddcJWT - ddc JWT to generate form
	 * @returns {String} - form layout
	 */
	private getIframeDDCLayout(ddcJWT) {
		return `
			<form name="devicedata" method="POST" action="${this.config.worldpayCardinalCommerceURL}/V1/Cruise/Collect" onload="this.submit();">
				<input type="hidden" name="Bin" value="" />
				<input type="hidden" name="JWT" value="${ddcJWT}" />
			</form>
		`;
	}

	private async getThreeDSDDC() {
		const response = await this.managerRepository.getCommerceApi().customShopperWorldpay.getDDC({});
		this.iframeLayout = this.getIframeDDCLayout(response.JWT);

		return promiseWithRetry(this.attemptGetDDC.bind(this), this.DDC_RETRIES_COUNT, {
			logProvider: this.managerRepository.getLogMgr().getLogProvider(),
			warningTitle: 'DDC request attempt',
			errorTitle: 'DDC request failed all retries'
		});

	}

	private attemptGetDDC() {
		return new Promise((resolve, reject) => {
			const iframe = this.createIframe({ body: this.iframeLayout });
			const worldpayCardinalCommerceURL = this.config.worldpayCardinalCommerceURL;

			window.addEventListener('message', onDDCResponse, false);

			const fallbackTimeout = setTimeout(() => {
				iframe.remove();
				window.removeEventListener('message', onDDCResponse, false);

				reject(new Error('DDC failed'));
			}, this.FALLBACK_DDC_TIME);

			function onDDCResponse(event) {
				if (event.origin === worldpayCardinalCommerceURL) {
					const data = JSON.parse(event.data);

					if (data && data.Status && data.SessionId) {
						iframe.remove();
						window.removeEventListener('message', onDDCResponse, false);

						resolve(data.SessionId);
						clearTimeout(fallbackTimeout);
					}
				}
			}

			(iframe.contentWindow.document.forms as any).devicedata.submit();
		});
	}

	/**
	 * Parse google name field to first name and last name
	 * @param {string} fullName - full name provided by google
	 */
	private parseGoogleName(fullName) {
		const parsedName = fullName.split(' ');
		const nameLength = parsedName.length;
		let firstName = '';
		let lastName = '';

		if (nameLength === 1) {
			firstName = parsedName[0];
			lastName = parsedName[0];
		}

		if (nameLength === 2) {
			firstName = parsedName[0];
			lastName = parsedName[1];
		}

		if (nameLength > 2) {
			firstName = parsedName.slice(0, 2).join(' ');
			lastName = parsedName.slice(2).join(' ');
		}

		return {
			firstName: firstName,
			lastName: lastName
		};
	}

}

export default GooglePayWorldpayHandler;
